import type { FE_ScheduleSession } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useOrgFeatures } from "@/api/organizations";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { PageHeader } from "@/components/PageHeader";
import { MultiSelect } from "@/components/ui/multiselect";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useAttendanceCalendarData } from "@/hooks/useScheduleSessions";
import { AttendanceCalendar } from "@/routes/_auth/app/_app/org/$orgId/attendance/-components/AttendanceCalendar";

// Storage key for persisting instructor filter preference (per organization)
const getInstructorFilterStorageKey = (orgId: string) =>
	`pilatestime-attendance-instructor-filter-${orgId}`;

// Helper functions for localStorage persistence
function getStoredInstructorFilter(orgId: string): string[] | null {
	try {
		const stored = localStorage.getItem(getInstructorFilterStorageKey(orgId));
		if (stored) {
			const parsed = JSON.parse(stored);
			if (
				Array.isArray(parsed) &&
				parsed.every((id) => typeof id === "string")
			) {
				return parsed;
			}
		}
	} catch {
		// Silently fail if localStorage is not available or data is invalid
	}
	return null;
}

function saveInstructorFilter(orgId: string, instructors: string[]): void {
	try {
		if (instructors.length > 0) {
			localStorage.setItem(
				getInstructorFilterStorageKey(orgId),
				JSON.stringify(instructors),
			);
		} else {
			localStorage.removeItem(getInstructorFilterStorageKey(orgId));
		}
	} catch {
		// Silently fail if localStorage is not available
	}
}

// Search params schema for instructor filter - supports multiple instructors
const attendanceSearchSchema = z.object({
	instructors: z.array(z.string()).optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/attendance/")({
	validateSearch: attendanceSearchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		context.queryClient.ensureQueryData(
			fetchOrganizationStaffQueryOptions(token, orgId),
		);

		return { userId, token, orgId };
	},
	component: AttendancePage,
});

function AttendancePage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const navigate = useNavigate();
	const { instructors: instructorsFromSearch } = Route.useSearch();

	// Get API parameters from route loader
	const { userId, token } = Route.useLoaderData();

	// Get organization features for calendar session title view mode and color legend
	const { calendarSessionTitleViewMode, calendarSessionColorLegend } =
		useOrgFeatures(userId, token, orgId);

	// Instructor filter - derived from URL search params, with localStorage fallback
	const selectedInstructors = instructorsFromSearch ?? [];

	// Track if we've restored from localStorage for this org (to prevent infinite loops)
	const hasRestoredRef = useRef<string | null>(null);

	// On mount: if no URL params, restore from localStorage
	useEffect(() => {
		// Only restore once per org, and only if no URL params are set
		if (hasRestoredRef.current !== orgId && !instructorsFromSearch) {
			hasRestoredRef.current = orgId;
			const storedInstructors = getStoredInstructorFilter(orgId);
			if (storedInstructors && storedInstructors.length > 0) {
				// Restore filters from localStorage to URL
				navigate({
					to: ".",
					search: { instructors: storedInstructors },
					replace: true,
				});
			}
		}
	}, [orgId, instructorsFromSearch, navigate]);

	// Sync filters to localStorage whenever they change
	useEffect(() => {
		saveInstructorFilter(orgId, selectedInstructors);
	}, [orgId, selectedInstructors]);

	// Fetch staff and establishments for filter dropdowns
	const { data: staff = [] } = useSuspenseQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);

	// Fetch schedule sessions using the new simplified hook
	const {
		data: eventSessions = [],
		isLoading,
		error,
		loadMonth,
		isMonthLoading,
	} = useAttendanceCalendarData(orgId);

	// Instructor filter handlers - updates URL search params and localStorage
	const handleInstructorChange = useCallback(
		(newSelectedInstructors: string[]) => {
			navigate({
				to: ".",
				search: {
					instructors:
						newSelectedInstructors.length > 0
							? newSelectedInstructors
							: undefined,
				},
				replace: true,
			});
		},
		[navigate],
	);

	// Filter events by selected instructors
	const filteredEventSessions =
		selectedInstructors.length === 0
			? eventSessions
			: eventSessions.filter((session: FE_ScheduleSession) =>
					selectedInstructors.includes(session.staffMemberId),
				);

	// Handle session click - navigate to session details page
	const handleEventSessionClick = (eventSession: FE_ScheduleSession) => {
		navigate({
			to: "/app/org/$orgId/attendance/session/$sessionId",
			params: { orgId, sessionId: eventSession.id },
		});
	};

	// Handle calendar navigation - load month when needed
	const handleCalendarNavigation = useCallback(
		async (currentDate: Date) => {
			await loadMonth(currentDate);
		},
		[loadMonth],
	);

	// Check if any month is currently loading
	const isAnyMonthLoading = useMemo(() => {
		// Check if current month or any recently navigated month is loading
		const now = new Date();
		const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
		const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
		const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

		return (
			isMonthLoading(currentMonth) ||
			isMonthLoading(nextMonth) ||
			isMonthLoading(prevMonth)
		);
	}, [isMonthLoading]);

	if (error) {
		return (
			<div className="space-y-6">
				<div className="alert alert-error">
					<span>Error loading attendance data: {error.message}</span>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header - responsive layout */}
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 px-2 py-1 sm:m-2">
				<PageHeader
					title={t("attendance.title")}
					description={t("attendance.description")}
					showBackButton={false}
				/>
			</div>

			{/* Filters and Controls - responsive width */}
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 px-2 sm:gap-4">
				{/* Instructor Filter and Filter Dialog */}
				<div className="flex items-center gap-2 w-full sm:w-auto">
					{/* Instructor Multiselect */}
					{staff.length > 0 && (
						<MultiSelect
							options={staff.map((instructor) => ({
								value: instructor.id,
								label:
									instructor.firstName && instructor.lastName
										? `${instructor.firstName} ${instructor.lastName}`
										: instructor.email,
								color: instructor.color,
							}))}
							value={selectedInstructors}
							onChange={handleInstructorChange}
							placeholder={t("attendance.instructorFilter.placeholder")}
							className="w-full sm:w-64"
							selectAllText={t("attendance.instructorFilter.selectAll")}
							deselectAllText={t("attendance.instructorFilter.deselectAll")}
							selectedCountText={t(
								"attendance.instructorFilter.selectedCount",
								{
									count: selectedInstructors.length,
								},
							)}
							maxDisplay={1}
						/>
					)}
				</div>
			</div>

			<div className="flex-1 h-full min-h-0">
				<AttendanceCalendar
					eventSessions={filteredEventSessions}
					isLoading={isLoading || isAnyMonthLoading}
					onEventSessionClick={handleEventSessionClick}
					onCalendarNavigation={handleCalendarNavigation}
					sessionTitleViewMode={calendarSessionTitleViewMode}
					colorLegend={calendarSessionColorLegend}
					orgId={orgId}
				/>
			</div>
		</div>
	);
}
